import axios from "axios";

import { wait } from "src/utils/wait";

class AuthApi {
  constructor() {
    this.meiliApiKey = process.env.REACT_APP_MEILI_API_KEY || "";
    this.meiliApiBaseUrl = process.env.REACT_APP_MEILI_API_BASE_URL || "";
    this.instanceApiBaseUrl = process.env.REACT_APP_INSTANCE_API_BASE_URL || "";
  }

  async createIndex(request) {
    const { uid, primaryKey } = request;
    await axios.post(`${this.meiliApiBaseUrl}/indexes`, {
      uid,
      primaryKey,
    });
  }

  async getIndex(request) {
    const { uid } = request;
    const response = await axios.get(`${this.meiliApiBaseUrl}/indexes`, {
      params: {
        offset: 0,
        limit: 5000,
      },
    });

    const findIndex = response.data.results.find(
      (result) => result.uid === uid,
    );

    return findIndex;
  }

  async updateSortableAttributes(request) {
    const { indexUid, attributes = [] } = request;
    await axios.put(
      `${this.meiliApiBaseUrl}/indexes/${indexUid}/settings/sortable-attributes`,
      attributes,
    );
  }

  async updatFilterableAttributes(request) {
    const { indexUid, attributes = [] } = request;
    await axios.put(
      `${this.meiliApiBaseUrl}/indexes/${indexUid}/settings/filterable-attributes`,
      attributes,
    );
  }

  async getUser(request = {}) {
    const { username } = request;

    const response = await axios.post(
      `${this.meiliApiBaseUrl}/indexes/user/search`,
      {
        q: username,
        offset: 0,
        limit: 1,
        filter: `username = "${username}"`,
        attributesToRetrieve: [
          "id",
          "createdAt",
          "updatedAt",
          "username",
          "name",
          "department",
          "role",
          "failCount",
          "status",
        ],
      },
    );

    if (response.data.hits.length <= 0) return null;

    return response.data.hits[0];
  }

  async signIn(request) {
    const { username, password } = request;

    const response = await axios.post(`${this.instanceApiBaseUrl}/signin`, {
      username,
      password,
    });

    return {
      id: response.data?.data?.id || "",
      accessToken: response.data?.data?.token || "",
    };
  }

  async signUp(request) {
    const { username, password, name, role, department } = request;

    const indexSettingPromises = [];
    const usersIndex = await this.getIndex({ uid: "user" });
    if (!usersIndex) {
      await this.createIndex({
        uid: "user",
        primaryKey: "id",
      });
    }
    indexSettingPromises.push(
      this.updateSortableAttributes({
        indexUid: "user",
        attributes: ["createdAt", "updatedAt"],
      }),
    );
    indexSettingPromises.push(
      this.updatFilterableAttributes({
        indexUid: "user",
        attributes: [
          "id",
          "createdAt",
          "updatedAt",
          "passwordAt",
          "username",
          "password",
          "role",
        ],
      }),
    );

    const statIndex = await this.getIndex({ uid: "stat" });
    if (!statIndex) {
      await this.createIndex({
        uid: "stat",
        primaryKey: "id",
      });
    }
    indexSettingPromises.push(
      this.updateSortableAttributes({
        indexUid: "stat",
        attributes: ["timestamp"],
      }),
    );
    indexSettingPromises.push(
      this.updatFilterableAttributes({
        indexUid: "stat",
        attributes: ["id", "instanceName", "timestamp"],
      }),
    );
    const fdStatIndex = await this.getIndex({ uid: "fdStat" });
    if (!fdStatIndex) {
      await this.createIndex({
        uid: "fdStat",
        primaryKey: "id",
      });
    }
    indexSettingPromises.push(
      this.updateSortableAttributes({
        indexUid: "fdStat",
        attributes: ["timestamp"],
      }),
    );
    indexSettingPromises.push(
      this.updatFilterableAttributes({
        indexUid: "fdStat",
        attributes: ["id", "instanceName", "timestamp"],
      }),
    );

    const realtimeStatIndex = await this.getIndex({ uid: "realtimeStat" });
    if (!realtimeStatIndex) {
      await this.createIndex({
        uid: "realtimeStat",
        primaryKey: "id",
      });
    }
    indexSettingPromises.push(
      this.updateSortableAttributes({
        indexUid: "realtimeStat",
        attributes: ["timestamp", "sort"],
      }),
    );
    indexSettingPromises.push(
      this.updatFilterableAttributes({
        indexUid: "realtimeStat",
        attributes: ["id", "type", "instanceName", "timestamp"],
      }),
    );

    if (indexSettingPromises.length > 0) {
      await wait(500);
      await Promise.all(indexSettingPromises);
    }

    let response = await axios.post(`${this.instanceApiBaseUrl}/signup`, {
      username,
      name,
      password,
      role,
      department,
    });

    await wait(500);

    response = await axios.post(`${this.instanceApiBaseUrl}/signin`, {
      username,
      password,
    });

    return {
      id: response.data.data?.id || "",
      accessToken: response.data.data?.token || "",
    };
  }

  async me(request) {
    const { accessToken } = request;

    let response = await axios.post(`${this.instanceApiBaseUrl}/verify`, {
      token: accessToken,
    });

    const id = response.data.data?.id || "";

    response = await axios.post(`${this.meiliApiBaseUrl}/indexes/user/search`, {
      q: id,
      offset: 0,
      limit: 1,
      filter: `id = "${id}"`,
      attributesToRetrieve: [
        "id",
        "createdAt",
        "updatedAt",
        "passwordAt",
        "username",
        "name",
        "department",
        "role",
        "failCount",
        "status",
      ],
    });

    if (response.data.hits.length <= 0) {
      const error = new Error(
        "인증 토큰이 유효하지 않습니다. 다시 시도해주세요.",
      );
      error.status = 401;
      error.code = "INVALID_AUTHORIZATION_TOKEN";
      throw error;
    }

    const user = response.data.hits[0];

    return user;
  }

  async resetPassword(request) {
    const { username, oldPassword, newPassword } = request;

    let response = await axios.post(`${this.instanceApiBaseUrl}/update`, {
      username,
      oldPassword,
      newPassword,
    });

    await wait(500);

    response = await axios.post(`${this.instanceApiBaseUrl}/signin`, {
      username,
      password: newPassword,
    });

    return {
      id: response.data?.data?.id || "",
      accessToken: response.data?.data?.token || "",
    };
  }

  async sendLog(request) {
    const {
      cmd = "",
      id = "",
      username = "",
      action = "",
      reason = "",
    } = request;

    await axios.post(`${this.instanceApiBaseUrl}/log`, {
      cmd,
      id,
      username,
      action,
      reason,
      timestamp: new Date().getTime(),
    });
  }

  async getAdminUsers() {
    const usersIndex = await this.getIndex({ uid: "user" });
    if (!usersIndex) {
      return {
        items: [],
        count: 0,
      };
    }

    const response = await axios.post(
      `${this.meiliApiBaseUrl}/indexes/user/search`,
      {
        q: "",
        offset: 0,
        limit: 5000,
        filter: `role = "admin" OR role = "super"`,
        attributesToRetrieve: [
          "id",
          "createdAt",
          "updatedAt",
          "passwordAt",
          "username",
          "name",
          "department",
          "role",
          "failCount",
          "status",
        ],
      },
    );

    const items = response.data.hits;
    const count = items.length;

    return {
      items,
      count,
    };
  }
}

export const authApi = new AuthApi();
